import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollToPlugin);
import tplCmsNoimage from '../templates/cms_noimage.pug';

/**
 * Replace placeholder text in a string based on a replacement list
 * 
 * @param {string} textContent - Input text
 * @param {string[]} replacementList - Replacemet values
 * @param {object} options - Optional settings
 * @param {RegExp} [options.pattern=/(%s)/gm] - Replacement placeholder pattern (%s)
 * 
 * @returns {string}
 * 
 * @example
 * formatText('%s world!', ['Hello']); // <- Hello world!
 */
export const formatText = (textContent, replacementList, options = {}) => {
  const _defaults = {
    pattern: /(%s)/gm,
  };
  const opts = { ..._defaults, ...options };
  let i = -1;
  return textContent.replace(opts.pattern, match => {
    i ++;
    return (typeof replacementList[i] !== 'undefined') ? replacementList[i] : match;
  });
};

/**
 * Check if HTML element has a specific tag
 * 
 * @param {HTMLElement} el - HTML element to check
 * @param {string} tagName - HTML tag name to check
 * 
 * @returns {boolean}
 */
export const elementHasTag = (el, tagName) => {
  return (el.tagName.toLowerCase().trim() === tagName.toLowerCase()) ? true : false;
};

/**
 * Find field value by ID in a list
 * 
 * @param {string} id - ID to search
 * @param {string} fieldName - Field name to return
 * @param {object[]} list - List to search
 * 
 * @returns {string|null}
 */
export const getDataFieldById = (id, fieldName, list) => {
  const item = list.find(item => item.id === id);
  if (!item) {
    return null;
  }
  return (typeof item[fieldName] !== 'undefined') ? item[fieldName] : null;
};

/**
 * Set page metadata
 * 
 * @param {object} data - Page metadata
 * @param {string|null} [data.meta_title=null] - Meta title
 * @param {string|null} [data.meta_description=null] - Meta description
 * @param {string|null} [data.og_title=null] - OpenGraph description
 * @param {string|null} [data.og_url=null] - OpenGraph URL
 * @param {string|null} [data.og_description=null] - OpenGraph description
 * @param {string|null} [data.og_image=null] - OpenGraph image
 * 
 * @returns {void}
 */
export const updatePageMeta = (data = {}) => {
  const _defaults = {
    meta_title: null,
    meta_description: null,
    og_title: null,
    og_url: null,
    og_description: null,
    og_image: null,
  };
  const meta = { ..._defaults, ...data };

  if (meta.meta_title) {
    document.title = meta.meta_title;
  }
  if (meta.meta_description) {
    document.querySelector('meta[name="description"]').setAttribute('content', meta.meta_description);
  }
  if (meta.og_title) {
    document.querySelector('meta[property="og:title"]').setAttribute('content', meta.og_title);
  }
  if (meta.og_url) {
    document.querySelector('meta[property="og:url"]').setAttribute('content', meta.og_url);
  }
  if (meta.og_description) {
    document.querySelector('meta[property="og:description"]').setAttribute('content', meta.og_description);
  }
  if (meta.og_image) {
    document.querySelector('meta[property="og:image"]').setAttribute('content', meta.og_image);
  }
};

/**
 * Scroll window to specific element
 * 
 * @param {string|HTMLElement} element - Target HTML element or CSS selector
 * @param {object} options - Optional settings
 * @param {number} [options.duration=1] - Scroll animation duration (seconds)
 * @param {string} [options.ease=linear] - Scroll animation easing (any valid GSAP ease type)
 * @param {number} [options.offset=0] - Scroll-to top offset
 * @param {null|function} [options.onComplete=null] - Custom callback function after animation completed
 * 
 * @returns {void}
 */
export const scrollTo = (element, options = {}) => {
  const _defaults = {
    duration: 1,
    ease: 'power2.inOut',
    offset: 0,
    onComplete: null,
  };
  const opts = { ..._defaults, ...options };
  const el = (typeof element === 'string') ? document.querySelector(element) : element;
  gsap.to(window, {
    duration: opts.duration,
    ease: opts.ease,
    scrollTo: {
      y: el,
      offsetY: opts.offset,
      autoKill: false,
    },
    onComplete: () => {
      if (typeof opts.onComplete === 'function') {
        opts.onComplete.call(null);
      }
    },
  });
};

/**
 * Noimage element output
 * 
 * @param {string} type - Image type (square | landscape)
 * @param {object} options - Optional settings
 * @param {string} [options.theme=light] - Image theme (light | dark)
 * @param {string} [options.classname] - Image theme CSS classname(s)
 * @param {string} [options.alt] - Image alt
 * @param {null|string} [options.loading=lazy] - Image loading attribute
 * 
 * @returns {string}
 */
export const cmsNoImage = (type, options = {}) => {
  const _defaults = {
    theme: 'light',
    classname: '',
    styles: '',
    alt: '',
    loading: 'lazy',
  };
  const opts = { ..._defaults, ...options };

  const map = {
    square: {
      light: [
        { src: 'static/img/noimage-square-light.webp', type: 'image/webp', width: 320, height: 320 },
        { src: 'static/img/noimage-square-light.jpg', type: 'image/jpeg', width: 320, height: 320 },
      ],
      dark: [
        { src: 'static/img/noimage-square-dark.webp', type: 'image/webp', width: 320, height: 320 },
        { src: 'static/img/noimage-square-dark.jpg', type: 'image/jpeg', width: 320, height: 320 },
      ],
    },
    landscape: {
      light: [
        { src: 'static/img/noimage-landscape-light.webp', type: 'image/webp', width: 700, height: 465 },
        { src: 'static/img/noimage-landscape-light.jpg', type: 'image/jpeg', width: 700, height: 465 },
      ],
      dark: [
        { src: 'static/img/noimage-landscape-dark.webp', type: 'image/webp', width: 700, height: 465 },
        { src: 'static/img/noimage-landscape-dark.jpg', type: 'image/jpeg', width: 700, height: 465 },
      ],
    },
    wide: {
      light: [
        { src: 'static/img/noimage-wide-light.webp', type: 'image/webp', width: 2600, height: 480 },
        { src: 'static/img/noimage-wide-light.jpg', type: 'image/jpeg', width: 2600, height: 480 },
      ],
      dark: [
        { src: 'static/img/noimage-wide-dark.webp', type: 'image/webp', width: 2600, height: 480 },
        { src: 'static/img/noimage-wide-dark.jpg', type: 'image/jpeg', width: 2600, height: 480 },
      ],
    },
  };
  
  const res = tplCmsNoimage({
    list: map[type][opts.theme],
    alt: opts.alt.trim(),
    classname: opts.classname.trim(),
    styles: opts.styles.trim(),
    loading: opts.loading.trim(),
  });

  return res;

  // let res = '<picture>';
  // map[type][opts.theme].forEach(img => {
  //   res += `<source srcset="/${img.src}" type="${img.type}">`;
  //   if (img.type === 'image/jpeg') {
  //     res += `<img src="/${img.src}" width="${img.width}" height="${img.height}" alt="${opts.alt.trim()}" class="${opts.classname.trim()}" style="${opts.styles}" loading="${opts.loading}">`;
  //   }
  // });
  // res += '</picture>';
  
  // return res;
};

export const contentToCollapse = (input, options = {}) => {
  const _defaults = {
    collapseId: 'collapseContent',
  };
  const opts = { ..._defaults, ...options };
  const res = {
    content: input,
    show_btn: false,
  };
  const doc = new DOMParser().parseFromString(input, 'text/html');
  const paragraphs = doc.querySelectorAll('p');
  if (paragraphs.length === 0) {
    return res;
  }
  res.content = '';
  Array.from(paragraphs).forEach((el, i) => {
    res.content += el.outerHTML;
    if (paragraphs.length > 1 && i === 0) {
      res.content += `<div class="collapse" id="${opts.collapseId}">`;
    }
  });
  if (paragraphs.length > 1) {
    res.content += '</div>';
    res.show_btn = true;
  }
  return res;
};

/**
 * Copy text to clipboard
 * 
 * @param {string} text - Text to copy
 * 
 * @returns {Promise}
 */
export const copyToClipboard = text => {
  return new Promise((resolve, reject) => {
    if (!navigator.clipboard) {
      copyToClipboardFallback(text)
        .then(() => resolve())
        .catch(() => reject());
    } else {
      navigator.clipboard.writeText(text)
        .then(() => resolve())
        .catch(() => reject());
    }
  });
};

const copyToClipboardFallback = text => {
  return new Promise((resolve, reject) => {
    let result;
    const textareaEl = document.createElement('textarea');
    textareaEl.value = text;
    textareaEl.style.position = 'fixed';
    textareaEl.style.top = 0;
    textareaEl.style.left = 0;
    textareaEl.style.opacity = 0;
    document.body.appendChild(textareaEl);
    textareaEl.focus();
    textareaEl.select();
    
    try {
      const copy = document.execCommand('copy');
      result = (copy) ? true : false;
    } catch(err) {
      result = false;
    }

    document.body.removeChild(textareaEl);

    if (result) {
      resolve();
    } else {
      reject();
    }
  });
};